import { ChangeEvent, useState } from "react";
import { useRateCalculator } from "../context/rate-calculator-state";
import useAccountEnhanced from "src/hooks/use-account-enhanced";
import { usePostUserFeedbackData } from "src/api/rate-calculator-query-options";
import { BidRates } from "src/types/bid-rates";
import { HiSpeakerphone, HiOutlineInformationCircle } from "react-icons/hi";
import { Modal } from "src/components/modal";

export function FeedbackButton({ bidRates }: { bidRates: BidRates | null }) {
    //State Context
    const rateCalculatorState = useRateCalculator();

    //MVT User Account
    const accountEnhanced = useAccountEnhanced();
    const userName = accountEnhanced.account.name;

    //Post from Query Options
    const postUserFeedbackMutation = usePostUserFeedbackData();

    //Local Component State Values
    const [errors, setErrors] = useState<formError>();
    const [hasGreatRateFeedback, setHasGreatRateFeedback] =
        useState<boolean>(false);
    const [hasAvgRateFeedback, setHasAvgRateFeedback] =
        useState<boolean>(false);
    const [hasLowRateFeedback, setHasLowRateFeedback] =
        useState<boolean>(false);
    const [feedbackData, setFeedBackData] = useState({
        feedbackTextBody: "",
    });
    const [loading, setLoading] = useState<boolean | undefined>(undefined);

    //Types
    type formError = {
        feedbackText: string;
    };

    //Toggle Form Buttons
    const toggleRate = (rateValue: string) => {
        switch (rateValue) {
            case "Good":
                setHasGreatRateFeedback((prevValue) => !prevValue);
                break;
            case "Avg":
                setHasAvgRateFeedback((prevValue) => !prevValue);
                break;
            case "Low":
                setHasLowRateFeedback((prevValue) => !prevValue);
                break;
            default:
                console.log("you toggled nothing?");
        }
    };

    //Open Modal
    const openModal = async (ModalName: string) => {
        (document.getElementById(ModalName) as HTMLDialogElement).showModal();
    };

    //Close Modal
    const closeModal = async (ModalName: string) => {
        (document.getElementById(ModalName) as HTMLDialogElement).close();
    };

    //Reset Values in Modal
    const resetFormValues = async () => {
        feedbackData.feedbackTextBody = "";
        const errors: formError = {
            feedbackText: "",
        };
        setErrors(errors);
        setHasGreatRateFeedback(false);
        setHasAvgRateFeedback(false);
        setHasLowRateFeedback(false);
    };

    //Handle text box input change
    const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFeedBackData((prevState) => ({ ...prevState, [name]: value }));
    };

    //Do you have Feedback?
    const validateFeedback = () => {
        var formisValid = true;

        const errors: formError = {
            feedbackText: "",
        };

        if (!feedbackData.feedbackTextBody) {
            errors.feedbackText = "feedback is required";
        }

        setErrors(errors);

        Object.values(errors).forEach((value) => {
            if (value) {
                formisValid = false;
            }
        });

        return formisValid;
    };

    //Submit form aka Post to API
    const handleFeedBackSubmit = async (
        event: ChangeEvent<HTMLFormElement>,
    ) => {
        event.preventDefault();

        if (validateFeedback()) {
            setLoading(true);
            await postUserFeedbackMutation.mutateAsync({
                FeedbackText: feedbackData.feedbackTextBody,
                UPN: userName,
                HighBid: hasGreatRateFeedback,
                LowBid: hasLowRateFeedback,
                AvgBid: hasAvgRateFeedback,
                BidRates: [
                    bidRates?.HighBid,
                    bidRates?.AvgBid,
                    bidRates?.LowBid,
                ],
                FormPayload: rateCalculatorState.currentFormData,
            });
            setLoading(false);
            closeModal("FeedbackModal");
            resetFormValues();
        }
    };

    return (
        <>
            <button
                className="btn btn-xs btn-outline"
                type="button"
                onClick={() => openModal("FeedbackModal")}
            >
                <span className="flex gap-2">
                    <HiSpeakerphone className="w-4 h-4" />
                    Send Feedback
                </span>
            </button>

            <Modal id="FeedbackModal" style="">
                <div className="flex flex-col gap-2">
                    <div>
                        <h3 className="font-bold text-lg">Provide Feedback</h3>
                        <span className="text-sm inline-block text-gray-700">
                            Select which Rates feedback applies to
                        </span>
                        <div
                            className="tooltip tooltip-bottom"
                            data-tip="Data from calculator form & calculated rates will be automatically included with submitted feedback."
                        >
                            <HiOutlineInformationCircle className="w-4 h-4 ml-1" />
                        </div>
                    </div>
                    <div className="flex flex-row gap-2">
                        <button
                            type="button"
                            className={`btn btn-success ${
                                hasGreatRateFeedback ? "" : "btn-dash"
                            }`}
                            onClick={() => toggleRate("Good")}
                        >
                            Great Rate
                        </button>
                        <button
                            type="button"
                            className={`btn btn-warning ${
                                hasAvgRateFeedback ? "" : "btn-dash"
                            }`}
                            onClick={() => toggleRate("Avg")}
                        >
                            Average Rate
                        </button>
                        <button
                            type="button"
                            className={`btn btn-error ${
                                hasLowRateFeedback ? "" : "btn-dash"
                            }`}
                            onClick={() => toggleRate("Low")}
                        >
                            Low Rate
                        </button>
                    </div>
                    {errors?.feedbackText && (
                        <p className="text-red-600 text-xs">
                            {errors?.feedbackText}
                        </p>
                    )}
                    <form
                        id="feedbackForm"
                        className="space-y-3"
                        onSubmit={handleFeedBackSubmit}
                    >
                        <textarea
                            className="textarea textarea-bordered w-full mt-2"
                            rows={3}
                            placeholder="Feedback comments"
                            name="feedbackTextBody"
                            value={feedbackData.feedbackTextBody}
                            onChange={handleTextChange}
                        />

                        <fieldset className="card-actions gap-4">
                            <button
                                className="btn btn-link btn-sm"
                                type="reset"
                                onClick={() => closeModal("FeedbackModal")}
                            >
                                Cancel
                            </button>

                            <button
                                className="btn btn-primary btn-sm \\"
                                type="submit"
                            >
                                {loading ? (
                                    <span className="loading loading-spinner"></span>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        </fieldset>
                    </form>
                </div>
            </Modal>
        </>
    );
}
